import WhatToPrepareList from "@/screens/Welcome/common/WhatToPrepare";
import ProductInfo from "@/screens/Welcome/common/ProductInfo";
import Title from "@/screens/Welcome/common/Title";
import classes from "./index.module.css";
import ProcessDescription from "@/screens/Welcome/common/ProcessDescription";
import { useTranslation } from "@/i18n/client";
import { Commodity, Process, Product } from "@/types/enumerations";
import { CustomerType } from "@/types/customerType";
import ContinueButton from "@/screens/common/ContinueButton";
import { Process as ProcessContract, ProcessSpecification, Segment } from "@/types/contract";
import Text from "@/components/Text";
import { RecontractingVariant } from "@/types/recontractingRequest";
import { Campaigns } from "@/types/schemas/campaigns";
import Banner from "./Banner";
import { Trans } from "react-i18next";
import Link from "@/components/Link";
import type { ProcessReasonType } from "@/types/processReason";
import { useSearchParams } from "next/navigation";
import { isS3PreviousCustomer } from "@/screens/Welcome/helpers";
import { useIsNewCustomerLink } from "@/hooks/useIsNewCustomerLink";

interface ContentProps {
  commodity: Commodity;
  process: Process;
  customerType?: CustomerType;
  product?: Product | null;
  onStart: () => void;
  selfService: boolean | null;
  isLoading?: boolean;
  processSpecification?: ProcessSpecification;
  recontractingVariant?: RecontractingVariant;
  campaign?: Campaigns | null;
  processReason?: ProcessReasonType | null;
  phase?: string | null;
  segment?: Segment;
  contractStatus?: string | null;
}

export const Content = ({
  commodity,
  process,
  customerType,
  product,
  onStart,
  selfService,
  isLoading = false,
  processSpecification,
  recontractingVariant,
  campaign,
  processReason,
  phase,
  segment,
  contractStatus,
}: ContentProps) => {
  const { t } = useTranslation("welcome");
  const searchParams = useSearchParams();
  const isNewCustomerLink = useIsNewCustomerLink();

  const isS1Process = process.value === ProcessContract.SUPPLIER_CHANGE;
  const isS2Process = process.value === ProcessContract.DPI;
  const isMSProcess = process.value === ProcessContract.MOBILITY_SERVICES;
  const isS1ProcessSelfService = isS1Process && selfService === true;
  const isTCRProcess = process.value === ProcessContract.TCR;
  const isR1ProcessSelfService = process.value === ProcessContract.NEW_POINT && selfService === true;
  const isZDPProcess = process.value === ProcessContract.ZDP;
  const isS3NewCustomerProcess = isS3PreviousCustomer(process.value, contractStatus, phase);
  const showProcessDescription =
    !isS3NewCustomerProcess &&
    !isS1Process &&
    !isR1ProcessSelfService &&
    !isMSProcess &&
    !isTCRProcess &&
    !isS2Process &&
    !isZDPProcess;
  const isZSPProcess = process.value === ProcessContract.RECONTRACTING;
  const showBanner = process.value === ProcessContract.RECONTRACTING && campaign;
  const companyTypeFromParams = searchParams.get("customer_type") === "company";
  const showWhatToPrepareList =
    !isS1ProcessSelfService &&
    !isZSPProcess &&
    !isS2Process &&
    !(isMSProcess && (companyTypeFromParams || customerType === "company" || selfService === false));
  const showProductInfo = product && !isZDPProcess && !isS3NewCustomerProcess;
  // const productRelevance = product?.value.relevance?.split(",") ?? [];
  // const isProductWithoutBonus = productRelevance.includes("no_bonus");
  // const showS1Text = isS1ProcessSelfService && !isProductWithoutBonus;
  const showStartButtonLabel =
    isS3NewCustomerProcess || isTCRProcess || isNewCustomerLink || (!!selfService && process.value !== ProcessContract.PS);

  return (
    <div className={classes.root}>
      <div className={classes.welcome}>
        <Title
          commodity={commodity.value}
          process={process}
          product={product}
          processSpecification={processSpecification}
          selfService={!!selfService}
          processReason={processReason}
          recontractingVariant={recontractingVariant}
          phase={phase}
          contractStatus={contractStatus}
        />
        {showProductInfo && (
          <ProductInfo process={process.value} commodityName={commodity.name} customerType={customerType} product={product} />
        )}
        {showBanner && (
          <Banner product={product} campaign={campaign} process={process.value} recontractingVariant={recontractingVariant} />
        )}
        {showWhatToPrepareList && (
          <WhatToPrepareList
            process={process.value}
            selfService={!!selfService}
            product={product}
            processReason={processReason}
            phase={phase}
            segment={segment}
            contractStatus={contractStatus}
          />
        )}
        {showProcessDescription && (
          <ProcessDescription
            process={process.value}
            selfService={!!selfService}
            product={product}
            processSpecification={processSpecification}
            recontractingVariant={recontractingVariant}
            processReason={processReason}
            phase={phase}
          />
        )}
        {/* {showS1Text && <BonusText />} */}
        {isTCRProcess && processReason === "demontage_01" && <Text variant="reading">{t("descriptionTCRDismantling")}</Text>}
        {isTCRProcess && (
          <Text variant="reading">
            <Trans
              t={t}
              i18nKey="descriptionTCR"
              components={{
                householdLink: <Link key="householdLink" href="https://www.eon.cz/domacnosti/zakaznicka-pece/prepis/" />,
                tradesmanLink: <Link key="tradesmanLink" href="https://www.eon.cz/zivnostnici/zakaznicka-pece/prepis/" />,
              }}
            />
          </Text>
        )}
        {isS3NewCustomerProcess && <Text variant="reading">{t("textS3NewCustomer")}</Text>}
        {isS2Process && (
          <Text variant="reading_medium" style={{ textAlign: "center" }}>
            {t("textS2")}
          </Text>
        )}
        <ContinueButton isLoading={isLoading} onClick={onStart}>
          {showStartButtonLabel ? t("start") : t("proceedToSign")}
        </ContinueButton>
      </div>
    </div>
  );
};
